import "./partnerships.css";
import { inView } from "motion";
import { prefersReducedMotion } from "@/scripts/common/util";

const SLOT_COUNT = 6;
// CCW order from upper-left, down the left, across bottom, up the right.
// 240° arc on the bottom, 120° gap at top. 48° between adjacent slots.
const ANGLES_DEG = [300, 252, 209, 151, 108, 60];
const CYCLE_INTERVAL = 5000;
const FADE_STAGGER = 120;
const FADE_DURATION = 600;
const LOAD_INITIAL_DELAY_MS = 500;
const LOAD_CENTER_DURATION_MS = 500;
const LOAD_CIRCLE_DURATION_MS = 600;
const LOAD_CIRCLE_STAGGER_MS = 130;
const LOAD_LOGO_STAGGER_MS = 110;

function unitVectorForAngle(deg) {
  const r = (deg * Math.PI) / 180;
  return { x: Math.sin(r), y: -Math.cos(r) };
}

function shuffleIndices(n) {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildSlots(container) {
  const slots = [];

  for (let i = 0; i < SLOT_COUNT; i++) {
    const { x, y } = unitVectorForAngle(ANGLES_DEG[i]);

    const slot = document.createElement("div");
    slot.className = "partnerships-circle__slot";
    slot.dataset.slotIndex = String(i);
    slot.style.setProperty("--ux", x.toFixed(4));
    slot.style.setProperty("--uy", y.toFixed(4));

    const img = document.createElement("img");
    img.className = "partnerships-circle__logo";
    img.alt = "";
    img.decoding = "async";
    img.loading = "lazy";

    slot.appendChild(img);
    container.appendChild(slot);
    slots.push({ el: slot, img });
  }

  return slots;
}

function setLogo(entry, logo) {
  if (!logo) return;
  entry.img.src = logo.url;
  entry.img.alt = logo.title || "";
}

function startCycle(slots, logos) {
  let offset = 0;
  let intervalId = null;
  let timeouts = [];

  const tick = () => {
    offset = (offset + SLOT_COUNT) % logos.length;

    slots.forEach((entry, i) => {
      const next = logos[(offset + i) % logos.length];

      const fadeOutAt = setTimeout(() => {
        entry.el.classList.remove("is-visible");

        const swapAt = setTimeout(() => {
          setLogo(entry, next);
          entry.el.classList.add("is-visible");
        }, FADE_DURATION);

        timeouts.push(swapAt);
      }, i * FADE_STAGGER);

      timeouts.push(fadeOutAt);
    });
  };

  const start = () => {
    if (intervalId !== null) return;
    intervalId = setInterval(tick, CYCLE_INTERVAL);
  };

  const stop = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    timeouts.forEach(clearTimeout);
    timeouts = [];
  };

  start();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      stop();
    } else {
      start();
    }
  });
}

async function playLoadAnimation(circleContainer, slots) {
  const circles = Array.from(circleContainer.querySelectorAll("svg circle")).sort(
    (a, b) => parseFloat(a.getAttribute("r")) - parseFloat(b.getAttribute("r")),
  );

  await wait(LOAD_INITIAL_DELAY_MS);

  circleContainer.classList.add("is-center-in");
  await wait(LOAD_CENTER_DURATION_MS);

  circles.forEach((c, i) => {
    c.style.transitionDelay = `${i * LOAD_CIRCLE_STAGGER_MS}ms`;
  });
  circleContainer.classList.add("is-circles-in");
  await wait((circles.length - 1) * LOAD_CIRCLE_STAGGER_MS + LOAD_CIRCLE_DURATION_MS);
  circles.forEach((c) => {
    c.style.transitionDelay = "";
  });

  const order = shuffleIndices(slots.length);
  order.forEach((slotIdx, n) => {
    setTimeout(() => slots[slotIdx].el.classList.add("is-visible"), n * LOAD_LOGO_STAGGER_MS);
  });
  await wait((order.length - 1) * LOAD_LOGO_STAGGER_MS + FADE_DURATION);
}

function initCircle(container) {
  let logos;
  try {
    logos = JSON.parse(container.dataset.logos || "[]");
  } catch {
    return;
  }
  if (!Array.isArray(logos) || !logos.length) return;

  const filled =
    logos.length < SLOT_COUNT ? Array.from({ length: SLOT_COUNT }, (_, i) => logos[i % logos.length]) : logos;

  const slots = buildSlots(container);
  slots.forEach((entry, i) => setLogo(entry, filled[i]));

  const circleContainer = container.closest("[data-partnerships-circle]");
  const shouldCycle = filled.length > SLOT_COUNT;

  if (prefersReducedMotion || !circleContainer) {
    if (circleContainer) circleContainer.classList.add("is-center-in", "is-circles-in");
    slots.forEach((s) => s.el.classList.add("is-visible"));
    if (shouldCycle) startCycle(slots, filled);
    return;
  }

  let played = false;
  inView(
    circleContainer,
    () => {
      if (played) return;
      played = true;
      playLoadAnimation(circleContainer, slots).then(() => {
        if (shouldCycle) startCycle(slots, filled);
      });
    },
    { margin: "0px 0px -10% 0px" },
  );
}

function init() {
  document.querySelectorAll("[data-logos]").forEach(initCircle);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
